import React, { ChangeEvent, ReactChildren, Component } from 'react';
import ReactTable, { FilterRender, Column } from 'react-table';
import { debounce, confirmPopupBefore, popupErrorFromServer } from '../util/helperFunctions';
import api from '../util/api';
import { Link } from '@reach/router';
import Animal from '../Entities/Animal';
import { Button } from 'bloomer/lib/elements/Button';
import styled from 'styled-components';
import { trackers } from '../util/analytics';

const RowActionBttn = styled(Button)`
  &.button {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 15px;
    width: 100%;
  }
`;

const Flex = styled.div`
  display: flex;
  flex-wrap: wrap;

  & > * {
    flex: 1 0 50%;
    margin: 0;
  }
`;

const ConstrainedInput = styled.input`
  max-width: 100%;
`;

interface RowProps {
  animal: Animal;
  refresh: () => Promise<any>;
}

const RowActions = ({ animal, refresh }: RowProps) => {
  let status;
  let statusIcon;
  if (animal.active) {
    status = 'Deactivate';
    statusIcon = 'fa-square-o';
  } else {
    status = 'Activate';
    statusIcon = 'fa-check-square-o';
  }
  const handleDelete = () =>
    confirmPopupBefore(`Are you sure you want to delete ${animal.name}?`, async () => {
      await animal.delete();
      refresh();
      trackers.manageAnimals.submit().CONFIRMED_DELETE_ANIMAL();
    });
  const handleToggle = async () => {
    await animal.toggleActive();
    refresh();
    trackers.manageAnimals.submit().TOGGLE_ACTIVE();
  };
  return (
    <Flex className="table-actions">
      <Link to={`/manageAnimals/edit/${animal.aNumber}`}>
        <RowActionBttn id={`button-${animal.aNumber}-edit`}>
          <i className="fa fa-pencil-square-o" />
          <p className="is-size-7">Edit</p>
        </RowActionBttn>
      </Link>
      <Link to={`/statistics/evaluations/${animal.aNumber}`}>
        <RowActionBttn id={`button-${animal.aNumber}-eval`}>
          <i className="fa fa-file-text-o" />
          <p className="is-size-7">Eval Data</p>
        </RowActionBttn>
      </Link>
      <RowActionBttn id={`button-${animal.aNumber}-toggle`} onClick={handleToggle}>
        <i className={`fa ${statusIcon}`} />
        <p className="is-size-7">{status}</p>
      </RowActionBttn>
      <Link to={`/manageAnimals/comments/${animal.aNumber}`}>
        <RowActionBttn id={`button-${animal.aNumber}-comments`}>
          <i className="fa fa-commenting-o" />
          <p className="is-size-7">Comments</p>
        </RowActionBttn>
      </Link>
      <RowActionBttn id={`button-${animal.aNumber}-remove`} onClick={handleDelete}>
        <i className="fa fa-trash-o" />
        <p className="is-size-7">Remove</p>
      </RowActionBttn>
    </Flex>
  );
};

/* eslint-disable react/display-name */
const FilterInput = ({
  type = 'text',
  placeholder = '',
  track,
}: {
  type: string;
  placeholder: string;
  track: () => any;
}): FilterRender => ({ filter, onChange }) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
    track();
  };
  return (
    <ConstrainedInput
      className="table-input is-size-7"
      onChange={handleChange}
      placeholder={placeholder}
      type={type}
      value={filter ? filter.value : ''}
    />
  );
};
/* eslint-enable react/display-name */

const FilterDropdown: FilterRender = ({ filter, onChange }) => {
  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
    trackers.manageAnimals.change().FILTER_ACTIVE();
  };
  return (
    <select
      className="table-input is-size-5"
      onChange={handleChange}
      style={{
        height: '100%',
        borderRadius: 0,
      }}
    >
      <option value=""> All </option>
      <option value="true">Active</option>
      <option value="false">inactive</option>
    </select>
  );
};

const HeaderCell = ({ children, noClick }: { children: ReactChildren | string; noClick: boolean }) => (
  <div className={`table-header is-size-4 ${noClick ? 'no-click' : null}`}>{children}</div>
);

const StyledCell = styled.div`
  position: relative;
  top: 50%;
  transform: translateY(-50%);
`;
const Cell = ({ children }: { children: ReactChildren | string }) => (
  <StyledCell className="table-cell has-text-centered is-size-6">{children}</StyledCell>
);

interface PropTypes {}
interface StateTypes {
  animals: Animal[];
  loading: boolean;
  pages: number | undefined;
  lastFetch: null | {
    pageSize: any;
    page: any;
    sorted: any;
    filtered: any;
  };
}

class AnimalManagementTable extends Component<PropTypes, StateTypes> {
  state = {
    animals: [],
    pages: undefined,
    loading: true,
    lastFetch: null,
  };

  refresh = async () => {
    const { lastFetch } = this.state;
    if (!lastFetch) return;
    this.handleFetch(lastFetch);
  };

  handleFetch = async (state: any) => {
    try {
      this.setState({
        loading: true,
      });

      const fetchSettings = {
        limit: state.pageSize,
        page: state.page + 1,
        sorted: JSON.stringify(state.sorted),
        filtered: JSON.stringify(state.filtered),
      } as PaginationParams;
      const data = await api.animals.get(fetchSettings);

      this.setState({
        animals: data.docs,
        pages: data.pages,
        loading: false,
        lastFetch: Object.assign({}, state),
      });
    } catch (err) {
      popupErrorFromServer(err);
    }
  };

  render() {
    const columns: Column[] = [
      {
        Header: <HeaderCell noClick={false}>aNumber</HeaderCell>,
        accessor: 'aNumber',
        Filter: FilterInput({
          type: 'number',
          placeholder: 'Filter by aNumber',
          track: debounce(trackers.manageAnimals.change().FILTER_ANUMBER, 1200),
        }),
        Cell: ({ value }) => <Cell>{value}</Cell>,
      },
      {
        Header: <HeaderCell noClick={false}>Name</HeaderCell>,
        accessor: 'name',
        Filter: FilterInput({
          type: 'text',
          placeholder: 'Filter by Name',
          track: debounce(trackers.manageAnimals.change().FILTER_NAME, 1200),
        }),
        Cell: ({ value }) => <Cell>{value}</Cell>,
      },
      {
        Header: <HeaderCell noClick={false}>Type</HeaderCell>,
        accessor: 'animalType',
        Filter: FilterInput({
          type: 'text',
          placeholder: 'Filter by Type',
          track: debounce(trackers.manageAnimals.change().FILTER_ANIMAL_TYPE, 1200),
        }),
        Cell: ({ value }) => <Cell>{value}</Cell>,
      },
      {
        Header: <HeaderCell noClick={false}>Status</HeaderCell>,
        accessor: 'active',
        Filter: FilterDropdown,
        Cell: props => <Cell>{props.value ? 'Active' : 'Inactive'}</Cell>,
      },
      {
        id: 'location', // must match DB column name
        Header: <HeaderCell noClick={true}>Location</HeaderCell>,
        accessor: (animal: Animal) => animal.getLocationName(),
        Filter: FilterInput({
          type: 'text',
          placeholder: 'Filter by Location',
          track: debounce(trackers.manageAnimals.change().FILTER_LOCATION, 1200),
        }),
        Cell: ({ value }) => <Cell>{value}</Cell>,
      },
      {
        Header: <HeaderCell noClick={false}>Actions</HeaderCell>,
        Filter: () => <div />,
        minWidth: 280,
        Cell: ({ original }) => <RowActions refresh={this.refresh} animal={original} />,
      },
    ];

    const debouncedHandleFetch = debounce(this.handleFetch, 200);

    const { animals, pages, loading } = this.state;
    return (
      <ReactTable
        data={animals}
        pages={pages}
        loading={loading}
        columns={columns}
        showPagination={true}
        className="-striped -highlight"
        defaultPageSize={15}
        manual={true}
        filterable={true}
        onFetchData={debouncedHandleFetch}
      />
    );
  }
}

export default AnimalManagementTable;
